import {REAUTH_REQUIRED_ERROR, ReauthCancelledError} from 'common/report/reauth';
import {ResponseError} from 'common/util/shared-client';

const REAUTH_REQUIRED_STATUS = 403;

/** `response` is null on a fetch-cache miss. */
async function isReauthRequired(response: Response | null): Promise<boolean> {
  if (response?.status !== REAUTH_REQUIRED_STATUS) {
    return false;
  }
  try {
    const body = await response.clone().json();
    return body?.error === REAUTH_REQUIRED_ERROR;
  } catch {
    return false;
  }
}

let pendingReauth: Promise<boolean> | null = null;

/**
 * Requests rejected while a prompt is already open wait on that prompt rather than opening
 * their own, which the dialog would refuse, so one confirmation retries all of them.
 */
function reauthenticate(): Promise<boolean> {
  pendingReauth ??= (async () => {
    // Loaded on demand: the dialog pulls in WebAuthn, and it calls the fetch helpers that use this module.
    const {openReauthDialog} = await import(`common/widgets/reauth-dialog/open-reauth-dialog`);
    return Boolean(await openReauthDialog());
  })().finally(() => {
    pendingReauth = null;
  });
  return pendingReauth;
}

async function retryAfterReauth(doFetch: () => Promise<Response>): Promise<Response> {
  if (!(await reauthenticate())) {
    throw new ReauthCancelledError();
  }
  return doFetch();
}

/**
 * Run `doFetch`, and when the server answers that the action needs a fresh step-up
 * reauth, prompt for one and run it once more. The guarded endpoint rejects before
 * acting, so the retry is safe. A cancelled prompt rejects with `ReauthCancelledError`;
 * a retry the server still rejects is returned as is.
 */
export async function fetchWithReauth(doFetch: () => Promise<Response>): Promise<Response> {
  let response: Response;
  try {
    response = await doFetch();
  } catch (err) {
    if (err instanceof ResponseError && (await isReauthRequired(err.response))) {
      return retryAfterReauth(doFetch);
    }
    throw err;
  }
  if (await isReauthRequired(response)) {
    return retryAfterReauth(doFetch);
  }
  return response;
}
